import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

function PositionsTable({ positions, loading }) {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  if (loading) {
    return (
      <div className="bg-gray-900/40 backdrop-blur-md rounded-2xl p-8 border border-gray-700/50">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-400"></div>
        </div>
      </div>
    );
  }

  if (!positions || positions.length === 0) {
    return (
      <div className="bg-gray-900/40 backdrop-blur-md rounded-2xl p-8 border border-gray-700/50">
        <p className="text-gray-400 text-center">No positions found</p>
      </div>
    );
  }

  // Calculate totals
  const totals = positions.reduce((acc, position) => {
    acc.totalCost += position.totalCost;
    acc.marketValue += position.marketValue;
    acc.unrealizedPL += position.unrealizedPL;
    return acc;
  }, { totalCost: 0, marketValue: 0, unrealizedPL: 0 });

  const totalPLPercent = totals.totalCost > 0 ? (totals.unrealizedPL / totals.totalCost) * 100 : 0;

  return (
    <div className="bg-gray-900/30 backdrop-blur-md rounded-2xl border border-gray-700/50 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700/50">
              <th className="text-left px-6 py-4 text-gray-400 font-medium text-sm uppercase tracking-wider">Symbol</th>
              <th className="text-right px-6 py-4 text-gray-400 font-medium text-sm uppercase tracking-wider">Qty</th>
              <th className="text-right px-6 py-4 text-gray-400 font-medium text-sm uppercase tracking-wider">Avg Cost</th>
              <th className="text-right px-6 py-4 text-gray-400 font-medium text-sm uppercase tracking-wider">Current</th>
              <th className="text-right px-6 py-4 text-gray-400 font-medium text-sm uppercase tracking-wider">Total Cost</th>
              <th className="text-right px-6 py-4 text-gray-400 font-medium text-sm uppercase tracking-wider">Market Value</th>
              <th className="text-right px-6 py-4 text-gray-400 font-medium text-sm uppercase tracking-wider">P&L</th>
              <th className="text-right px-6 py-4 text-gray-400 font-medium text-sm uppercase tracking-wider">P&L %</th>
              <th className="text-right px-6 py-4 text-gray-400 font-medium text-sm uppercase tracking-wider">Day Change</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((position, index) => (
              <tr key={position.symbol} className="border-b border-gray-700/30 hover:bg-gray-800/20 transition-colors">
                <td className="px-6 py-4">
                  <div>
                    <div className="text-white font-medium">{position.symbol}</div>
                    <div className="text-gray-400 text-sm truncate max-w-[200px]">{position.description}</div>
                  </div>
                </td>
                <td className="text-right px-6 py-4 text-white">
                  {formatNumber(position.quantity)}
                </td>
                <td className="text-right px-6 py-4 text-white">
                  {formatCurrency(position.averageCost)}
                </td>
                <td className="text-right px-6 py-4 text-white">
                  {formatCurrency(position.currentPrice)}
                </td>
                <td className="text-right px-6 py-4 text-white">
                  {formatCurrency(position.totalCost)}
                </td>
                <td className="text-right px-6 py-4 text-white">
                  {formatCurrency(position.marketValue)}
                </td>
                <td className={`text-right px-6 py-4 font-medium ${position.unrealizedPL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  <div className="flex items-center justify-end gap-1">
                    {position.unrealizedPL >= 0 ? (
                      <TrendingUp className="w-4 h-4" />
                    ) : (
                      <TrendingDown className="w-4 h-4" />
                    )}
                    {formatCurrency(Math.abs(position.unrealizedPL))}
                  </div>
                </td>
                <td className={`text-right px-6 py-4 font-medium ${position.unrealizedPLPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatPercent(position.unrealizedPLPercent)}
                </td>
                <td className={`text-right px-6 py-4 ${position.dayChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  <div>
                    <div>{formatCurrency(Math.abs(position.dayChange))}</div>
                    <div className="text-sm">{formatPercent(position.dayChangePercent)}</div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-600">
              <td colSpan="4" className="px-6 py-4 text-white font-bold">
                Total
              </td>
              <td className="text-right px-6 py-4 text-white font-bold">
                {formatCurrency(totals.totalCost)}
              </td>
              <td className="text-right px-6 py-4 text-white font-bold">
                {formatCurrency(totals.marketValue)}
              </td>
              <td className={`text-right px-6 py-4 font-bold ${totals.unrealizedPL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                <div className="flex items-center justify-end gap-1">
                  {totals.unrealizedPL >= 0 ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                  {formatCurrency(Math.abs(totals.unrealizedPL))}
                </div>
              </td>
              <td className={`text-right px-6 py-4 font-bold ${totalPLPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatPercent(totalPLPercent)}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default PositionsTable;